import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../context/ContentContext';
import { CheckCircle2, ShieldCheck, FileText, TrendingUp, Zap, ArrowRight } from 'lucide-react';

const Services: React.FC = () => {
  const { content } = useContent();
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const getIcon = (name: string) => {
    switch (name) {
      case 'ShieldCheck': return ShieldCheck;
      case 'FileText': return FileText;
      case 'TrendingUp': return TrendingUp;
      case 'Zap': return Zap;
      default: return ShieldCheck;
    }
  };

  return (
    <section id="services" ref={sectionRef} className="py-32 bg-white text-slate-900 relative overflow-hidden">
      {/* 视差背景图 */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img 
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1920" 
          alt="Services Background" 
          className="w-full h-[120%] object-cover opacity-[0.03] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white"></div>
      </motion.div>

      {/* 装饰性背景层 - 稍微调低透明度以融合新背景 */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 opacity-60">
        <div className="absolute top-[15%] right-[2%] w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-[10%] left-[2%] w-[400px] h-[400px] bg-slate-100 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-32"
        >
          <div className="inline-block px-4 py-1.5 bg-slate-900 text-[#D4AF37] rounded-full text-[10px] font-black uppercase tracking-[0.4em] mb-6 shadow-sm">
            Service Spectrum
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-slate-900 tracking-tight">核心业务范围</h2>
          <div className="w-20 h-1.5 bg-[#D4AF37] mx-auto rounded-full"></div>
          <p className="mt-8 text-slate-500 max-w-2xl mx-auto leading-relaxed text-lg font-medium">
            依托全品类专业资质，我们构建了覆盖企业全生命周期的服务矩阵，助力企业从“被动合规”转向“主动增值”。
          </p>
        </motion.div>

        <div className="space-y-48">
          {content.services.map((service, index) => {
            const Icon = getIcon(service.iconName || 'ShieldCheck');
            const isEven = index % 2 === 0;
            const bgImage = service.image || "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&q=80&w=1200";
            
            return (
              <div 
                key={index} 
                className={`flex flex-col lg:flex-row gap-20 lg:gap-28 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}
              >
                {/* 视觉展示区域 */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, scale: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="w-full lg:w-1/2 relative group"
                >
                  <div className={`absolute -inset-8 bg-slate-50 rounded-[3rem] transform ${isEven ? 'rotate-2' : '-rotate-2'} transition-transform duration-1000 group-hover:rotate-0 -z-10`}></div>
                  <div className={`absolute -inset-4 bg-[#D4AF37]/5 rounded-[2.8rem] transform ${isEven ? '-rotate-1' : 'rotate-1'} transition-transform duration-1000 group-hover:rotate-0 -z-10`}></div>
                  
                  <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)] border-8 border-white">
                    <img 
                      src={bgImage} 
                      alt={service.title} 
                      loading="lazy"
                      className="w-full h-[450px] lg:h-[600px] object-cover transition-transform duration-[1.5s] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-700"></div>
                    <div className={`absolute ${isEven ? 'right-10' : 'left-10'} bottom-10 p-8 bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl flex flex-col items-center justify-center text-[#D4AF37] border border-white transition-all duration-700 group-hover:translate-y-[-10px]`}>
                      <Icon size={48} strokeWidth={1} />
                      <div className="mt-2 h-0.5 w-8 bg-[#D4AF37]/30 rounded-full"></div>
                    </div>
                  </div>

                  <div className={`absolute -top-12 ${isEven ? '-right-4' : '-left-4'} hidden lg:block`}>
                    <span className="text-[120px] font-serif font-black text-slate-50 leading-none select-none tracking-tighter">
                      0{index + 1}
                    </span>
                  </div>
                </motion.div>

                {/* 文字内容区域 */}
                <motion.div 
                  initial={{ opacity: 0, x: isEven ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="w-full lg:w-1/2"
                >
                  <div className="mb-10">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-1 bg-[#D4AF37] rounded-full"></div>
                      <span className="text-[#D4AF37] font-bold text-sm tracking-[0.3em] uppercase">Sector Focus</span>
                    </div>
                    <h3 className="text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-xl leading-relaxed font-medium">
                      {service.description}
                    </p>
                  </div>

                  <div className="space-y-8">
                    {service.items.map((item, idx) => {
                      const hasCategory = item.includes('【');
                      const [category, contentText] = hasCategory ? item.split('】') : ['', item];
                      
                      return (
                        <motion.div 
                          key={idx} 
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.4 + idx * 0.1 }}
                          className="group/item flex items-start gap-6"
                        >
                          <div className="mt-1.5 flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center group-hover/item:bg-[#D4AF37] transition-all duration-500 shadow-lg shadow-slate-900/10">
                            <CheckCircle2 size={14} className="text-[#D4AF37] group-hover/item:text-white" />
                          </div>
                          <div className="flex-1">
                             {hasCategory && (
                               <div className="inline-block px-3 py-0.5 bg-slate-100 text-[#B8860B] text-[10px] font-black rounded uppercase tracking-widest mb-2 group-hover/item:bg-[#D4AF37]/10 transition-colors">
                                 {category.replace('【', '')}
                               </div>
                             )}
                             <p className="text-slate-500 text-lg leading-relaxed group-hover/item:text-slate-900 transition-colors duration-300">
                               {contentText}
                             </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;